import {
  DOWNLOAD_CONTRACT,
  get_headers,
  get_project_id,
  get_token,
  MOVE_TO_TRUST_DIRECTORY_URL,
  PENDING_MASTER_SUBMISSIONS_URL,
  SUBMIT_TO_MASTER_URL,
  SUBMITTED_MASTER_SUBMISSIONS_URL,
  UPLOAD_LETTER_OF_AUTHORITY
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import { validate_non_sub_zero_number } from '@/utils/validations/validate_inputs';
import axios from "axios";
import FolderList from "@/components/probaworkflows_base_components/FolderList/FolderList.vue";

export default {
  name: 'MasterSubmissions',
  components: { FolderList },
  mixins: [alert_mixin, formatting_mixins],

  data: () => ({
    token: '',
    project_id: '',
    headers: {},
    pending_count: 0,
    submitted_count: 0,
    current_table: 'pending',
    pending_applications_list: [],
    submitted_applications_list: [],
    displayed_applications_list: [],
    application_row_map: {},
    selected_application: null,
    reference_number: '',
    trust_number: '',
    upload_file: null,
    is_submitting: false,
    is_uploading: false,
    is_completing: false,
    _is_mounted: false,
    upload_letter_error: null,
    is_exporting: false,
    export_error: null,
    status_cards: [
      {
        title: "Pending Master",
        color_class: "text-primary",
        type: "pending",
        icon: "fa-solid fa-list",
        count: 0,
      },
      {
        title: "Submitted Master",
        color_class: "green-text",
        type: "submitted",
        icon: "fa-solid fa-check-double",
        count: 0,
      }
    ],
    selected_status: "",
    show_filters: false,
    filters: {
      search: "",
      date_from: "",
      date_to: "",
    }
  }),

  created() {
    this.token = get_token();
    this.project_id = get_project_id();
    this.headers = get_headers();

    if (!validate_non_sub_zero_number(parseInt(this.project_id, 10))) {
      this.show_error('Project ID must not be null or zero');
      return;
    }
  },

  mounted() {
    this._is_mounted = true;
    this.fetch_pending_applications();
    this.fetch_submitted_applications();
    this.show_pending_table();
  },

  methods: {
    async fetch_pending_applications() {
      const params = { project_id: this.project_id };

      try {
        const response = await axios.get(PENDING_MASTER_SUBMISSIONS_URL, {
          headers: this.headers,
          params: params
        });

        if (!this._is_mounted) return;

        if (response.data.status === 'success') {
          this.pending_applications_list = response.data.data || [];
          this.prep_pending_table();
          this.pending_count = this.pending_applications_list.length;
          this.status_cards[0].count = this.pending_count;

          if (this.current_table === 'pending') {
            this.displayed_applications_list = [...this.pending_applications_list];
          }
        } else {
          this.show_error('Failed to fetch pending applications');
        }
      } catch (error) {
        this.show_error('Error fetching pending applications: ' + (error.response?.data?.message || error.message));
      }
    },

    async fetch_submitted_applications() {
      const params = { project_id: this.project_id };

      try {
        const response = await axios.get(SUBMITTED_MASTER_SUBMISSIONS_URL, {
          headers: this.headers,
          params: params
        });

        if (!this._is_mounted) return;

        if (response.data.status === 'success') {
          this.submitted_applications_list = response.data.data || [];
          this.prep_submitted_table();
          this.submitted_count = this.submitted_applications_list.length;
          this.status_cards[1].count = this.submitted_count;

          if (this.current_table === 'submitted') {
            this.displayed_applications_list = [...this.submitted_applications_list];
          }
        } else {
          this.show_error('Failed to fetch submitted applications');
        }
      } catch (error) {
        this.show_error('Error fetching submitted applications: ' + (error.response?.data?.message || error.message));
      }
    },

    prep_pending_table() {
      if (this.pending_applications_list?.length > 0) {
        this.application_row_map = {};
        this.pending_applications_list = this.pending_applications_list.map(app => {
          const applicationData = {
            id: app.id,
            "Application No": app.id,
            "Entity Name": app.entity_name || app.name || 'Unnamed Entity',
            "Entity Type": app.entity_type_name || 'Master Submission',
            "Date Created": app.date_created || new Date().toISOString(),
            Status: app.application_status || app.status || 'Pending',
            _attributes: app.attributes || [],
            is_child: false,
            has_children: false,
            children: [],
            _is_pending: true // Flag to identify pending applications
          };

          this.application_row_map[app.id] = applicationData;
          return applicationData;
        });
      }
    },

    prep_submitted_table() {
      if (this.submitted_applications_list?.length > 0) {
        this.application_row_map = {};
        this.submitted_applications_list = this.submitted_applications_list.map(app => {
          const applicationData = {
            id: app.id,
            "Application No": app.id,
            "Entity Name": app.entity_name || app.name || 'Unnamed Entity',
            "Entity Type": app.entity_type_name || 'Master Submission',
            "Date Created": app.date_created || new Date().toISOString(),
            "Reference Number": app.reference_number || 'N/A',
            Status: this.format_question_type(app.application_status || app.status || 'Submitted'),
            _attributes: app.attributes || [],
            is_child: false,
            has_children: false,
            children: [],
            _is_submitted: true // Flag to identify submitted applications
          };

          this.application_row_map[app.id] = applicationData;
          return applicationData;
        });
      }
    },

    show_pending_table() {
      this.current_table = 'pending';
      this.selected_status = 'pending';
      this.displayed_applications_list = [...this.pending_applications_list];
      this.on_filters_changed(this.filters);
    },

    show_submitted_table() {
      this.current_table = 'submitted';
      this.selected_status = 'submitted';
      this.displayed_applications_list = [...this.submitted_applications_list];
      this.on_filters_changed(this.filters);
    },

    on_filters_changed(new_filters) {
      this.filters = { ...this.filters, ...new_filters };

      let sourceList = [];
      if (this.current_table === 'pending') {
        sourceList = this.pending_applications_list;
      } else {
        sourceList = this.submitted_applications_list;
      }

      let filtered = [...sourceList];

      // Search filter
      const query = (this.filters.search || "").toLowerCase();
      if (query) {
        filtered = filtered.filter(app =>
          (app["Entity Name"] || "").toLowerCase().includes(query) ||
          (app["Entity Type"] || "").toLowerCase().includes(query) ||
          String(app["Application No"] || "").includes(query)
        );
      }

      // Date filters
      if (this.filters.date_from) {
        const from = new Date(this.filters.date_from);
        filtered = filtered.filter(app => new Date(app["Date Created"]) >= from);
      }
      if (this.filters.date_to) {
        const to = new Date(this.filters.date_to);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter(app => new Date(app["Date Created"]) <= to);
      }

      this.displayed_applications_list = filtered;
    },

    // Action methods
    async submit_application(row) {
      this.is_submitting = true;
      this.selected_application = row;

      try {
        const response = await axios.post(
          SUBMIT_TO_MASTER_URL,
          {
            application_id: row.id,
            project_id: this.project_id
          },
          { headers: this.headers }
        );

        if (response.data.status === 'success') {
          const ref_number = response.data.data.reference_number;
          this.show_success(`Application submitted successfully! Your reference number is: ${ref_number}`);
          await this.fetch_pending_applications();
          await this.fetch_submitted_applications();
          this.show_submitted_table();
        } else {
          this.show_error(response.data.message || 'Failed to submit');
        }
      } catch (error) {
        this.show_error('Error submitting: ' + (error.response?.data?.message || error.message));
      } finally {
        this.is_submitting = false;
      }
    },

    handle_upload(event) {
      this.upload_file = event.target.files[0];
    },

    async upload_letter_of_authority(row) {
      this.is_uploading = true;
      this.upload_letter_error = null;
      this.selected_application = row;

      if (!this.upload_file) {
        this.is_uploading = false;
        this.show_error("Please select a PDF file to upload");
        return;
      }

      const form_data = new FormData();
      form_data.append('application_id', row.id);
      form_data.append('document', this.upload_file);

      try {
        const response = await axios.post(
          UPLOAD_LETTER_OF_AUTHORITY,
          form_data,
          {
            headers: {
              ...this.headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        this.show_success(response.data.message || "Letter of Authority uploaded successfully!");
        await this.fetch_submitted_applications();
        this.close_modal('upload_letter_modal');

      } catch (error) {
        this.upload_letter_error = error.response?.data?.message ||
          error.response?.data?.error ||
          error.message;
        this.show_error(this.upload_letter_error);
      } finally {
        this.is_uploading = false;
      }
    },

    async complete_application(row) {
      this.selected_application = row;

      if (!this.trust_number) {
        this.show_error('Trust Number is required.');
        return;
      }

      this.is_completing = true;

      try {
        const response = await axios.post(
          MOVE_TO_TRUST_DIRECTORY_URL,
          {
            application_id: row.id,
            trust_number: this.trust_number,
            project_id: this.project_id
          },
          { headers: this.headers }
        );

        if (response.data.status === 'success') {
          this.show_success(`Completed: ${row["Entity Name"]} with Trust Number: ${this.trust_number}`);
          await this.fetch_submitted_applications();
          this.close_modal('complete_application_modal');
        } else {
          this.show_error(response.data.message || 'Failed to complete');
        }
      } catch (error) {
        this.show_error('Error completing: ' + (error.response?.data?.message || error.message));
      } finally {
        this.is_completing = false;
      }
    },

    async export_application(row) {
      this.is_exporting = true;
      this.export_error = null;

      if (!row?.id) {
        this.is_exporting = false;
        this.show_error("Missing application ID");
        return;
      }

      const response = await axios.post(
        DOWNLOAD_CONTRACT,
        {
          application_id: row.id,
          project_id: this.project_id
        },
        {
          headers: this.headers,
          responseType: 'blob'
        }
      ).catch(async (error) => {
        let error_message = 'Error exporting contract';
        if (error.response) {
          if (error.response.data instanceof Blob) {
            error_message += ': ' + await this.parse_blob_response(error.response.data);
          } else {
            error_message += ': ' + (error.response.data?.message || error.response.statusText);
          }
        } else {
          error_message += ': ' + error.message;
        }
        this.show_error(error_message);
        this.is_exporting = false;
        return null;
      });

      if (!response) {
        return;
      }

      if (response.status === 200 && response.data.type === 'application/pdf') {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contract_${row.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.show_success(`Contract exported for ${row["Entity Name"]}`);
      } else {
        const error_text = await this.parse_blob_response(response.data);
        this.show_error(error_text || 'Unexpected response from server');
      }

      this.is_exporting = false;
    },

    async parse_blob_response(blob) {
      const text = await blob.text().catch(() => 'Could not read server response');
      try {
        const json = JSON.parse(text);
        return json.message || json.error || text;
      } catch {
        return text;
      }
    },

    // Modal methods
    open_upload_modal(row) {
      this.selected_application = row;
      this.upload_file = null;
      this.upload_letter_error = null;
      this.open_modal('upload_letter_modal');
    },

    open_complete_modal(row) {
      this.selected_application = row;
      this.trust_number = '';
      this.open_modal('complete_application_modal');
    },

    open_modal(id) {
      const modal = document.getElementById(id);
      if (modal) {
        modal.style.display = 'block';
        modal.classList.add('show');
      }
    },

    close_modal(id) {
      const modal = document.getElementById(id);
      if (modal) {
        modal.style.display = 'none';
        modal.classList.remove('show');
      }
      if (id === 'complete_application_modal') this.trust_number = '';
    },
  }
};